'use client';

import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslations } from 'next-intl';
import { useUnit } from 'effector-react';
import classNames from 'classnames';
import { Icon, IconsMap } from '@nx-frontend/shared/ui/icon';
import { Spinner } from '@nx-frontend/shared/ui/spinner';
import { useToast } from '@nx-frontend/shared/ui/toast';
import { useDebounce } from '@nx-frontend/shared/hooks';
import { $unleashFlags } from '@nx-frontend/shared/store/unleash-flags';
import { PaywallPopupReason } from '@nx-frontend/shared/types';
import { UnleashFeatures } from '@nx-frontend/shared/types';
import { BillingPlanCodeWithoutPeriod, PaywallMode } from '@nx-frontend/shared/types/paywall-types';
import { getPaywallActionDiscounts } from './action-discounts';
import {
  trackTrialEndShowPopup,
  trackTrialStartShowPopup,
  trackUpgradePopupClose,
  trackUpgradeShowPopup,
} from './analytics';
import { PaywallAddons } from './paywall-addons';
import { PaywallCloseButton } from './paywall-close-button';
import { PaywallContext } from './paywall-context';
import { PaywallErrorDialog } from './paywall-error-dialog';
import { PaywallFooter } from './paywall-footer';
import { PaywallHeader } from './paywall-header';
import { PaywallOrderSummary } from './paywall-order-summary';
import { PaywallPlans } from './paywall-plans';
import { PaywallRibbon } from './paywall-ribbon';
import { PaywallToast } from './paywall-toast';
import { PaywallXmasTree } from './paywall-xmas-tree';
import './paywall.css';
import {
  $paywallStore,
  fetchPaywallEvent,
  type PaywallStateSettings,
  initPaywallSettingsEvent,
  applyCouponEvent,
} from './store';

export interface PaywallProps {
  mode: PaywallMode;
  title?: string;
  paywallAdditionPaymentParams: Record<string, string>;
  reason: PaywallPopupReason;
  isAppsumo: boolean;
  settings: PaywallStateSettings;
  showFreeTrial?: boolean;
  onClose: () => void;
  subscriptionType?: BillingPlanCodeWithoutPeriod;
}

export function Paywall(props: PaywallProps) {
  const { mode, title, onClose, paywallAdditionPaymentParams, reason, isAppsumo, showFreeTrial, subscriptionType } =
    props;

  const t = useTranslations('features');

  const [paywallState, unleashFlags] = useUnit([$paywallStore, $unleashFlags]);
  const [fetchPaywall, initPaywallSettings, applyCoupon] = useUnit([
    fetchPaywallEvent,
    initPaywallSettingsEvent,
    applyCouponEvent,
  ]);

  const [openTime, setOpenTime] = useState(0);

  const { toast } = useToast();

  const actionDiscounts = useMemo(() => {
    if (isAppsumo) {
      return null;
    }
    return unleashFlags[UnleashFeatures.SHOW_DISCOUNT_ACTION] ? getPaywallActionDiscounts() : null;
  }, [unleashFlags, isAppsumo]);

  const onCloseTrackClosePaywall = useCallback(
    (closeReason?: string) => {
      if (mode === 'upgrade') {
        trackUpgradePopupClose(reason, Math.round((Date.now() - openTime) / 1000));
      }
      // trigger global event
      const globalEvent = new CustomEvent('paywallPopupClose', {
        detail: { reason: typeof closeReason === 'string' ? closeReason : null },
      });
      document.dispatchEvent(globalEvent);
    },
    [mode, openTime, reason]
  );

  const onClosePaywall = useCallback(
    (closeReason?: string) => {
      onClose();
      onCloseTrackClosePaywall(closeReason);
    },
    [onClose, onCloseTrackClosePaywall]
  );

  const showCouponAppliedToast = useCallback(() => {
    toast({
      description: <PaywallToast description={t('paywall.coupon_has_been_applied')} />,
    });
  }, [toast, t]);

  const showSuccessToast = useCallback(
    (description: string) => {
      const title =
        mode === 'addons' ? t('paywall.addons_purchase_successful') : t('paywall.your_play_has_been_upgraded');
      toast({
        description: <PaywallToast title={title} description={description} refresh={true} />,
        duration: Infinity,
        showClose: false,
        style: { width: '502px' },
      });
    },
    [toast, mode, t]
  );

  const showErrorToast = useCallback(
    (description: string) => {
      toast({
        description: <PaywallToast description={description} type="error" />,
        duration: 20e3,
      });
    },
    [toast]
  );

  const trackPaywallOpenEvents = useCallback(() => {
    switch (mode) {
      case 'trialStart':
        trackTrialStartShowPopup();
        break;
      case 'trialEnd':
        trackTrialEndShowPopup();
        break;
      case 'upgrade':
        trackUpgradeShowPopup(reason);
        break;
    }
  }, [mode, reason]);

  const trackOncePaywallOpenEvents = useDebounce<void>(trackPaywallOpenEvents, 100);

  const coupon = useMemo(() => {
    return actionDiscounts?.coupon || paywallAdditionPaymentParams['coupon'];
  }, [actionDiscounts?.coupon, paywallAdditionPaymentParams]);

  useEffect(() => {
    setOpenTime(Date.now());
    initPaywallSettings({
      mode,
      settings: { ...props.settings, showCouponAppliedToast, showSuccessToast, showErrorToast, closePaywall: onClose },
    });
    fetchPaywall(() => {
      if (coupon) {
        applyCoupon(coupon);
      }
    });
    trackOncePaywallOpenEvents();
    const onDocumentKeydown = function (event: KeyboardEvent) {
      if (event.key === 'Escape') {
        onCloseTrackClosePaywall();
      }
    };
    document.body.addEventListener('keydown', onDocumentKeydown);
    return () => {
      document.body.removeEventListener('keydown', onDocumentKeydown);
    };
    // call effect onece on mount
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isFreePlan = !paywallState.currentPlanCode;

  const [orderSummaryShownOnce, setOrderSummaryShownOnce] = useState(false);
  const showOrderSummary = useMemo(() => {
    if (orderSummaryShownOnce && !isFreePlan) {
      return true;
    }
    if (mode === 'addons') {
      return Object.keys(paywallState.selectedAddons).length !== 0;
    }
    return mode === 'upgrade' && !isFreePlan && !isAppsumo;
  }, [isAppsumo, isFreePlan, mode, paywallState.selectedAddons, orderSummaryShownOnce]);

  useEffect(() => {
    if (showOrderSummary) {
      setOrderSummaryShownOnce(true);
    }
  }, [showOrderSummary]);

  return (
    <PaywallContext.Provider
      value={{
        mode,
        title,
        showOrderSummary,
        isFreePlan,
        paywallAdditionPaymentParams: {
          ...paywallAdditionPaymentParams,
          ...(coupon ? { coupon } : {}),
        },
        reason,
        isAppsumo,
        showFreeTrial,
        subscriptionType,
        action: actionDiscounts,
        onClose: onClosePaywall,
        getTimeDelta: () => Math.round((Date.now() - openTime) / 1000),
      }}
    >
      <div
        className={classNames('relative inline-flex flex-row rounded-xl w-full flex-shrink-0', {
          'min-h-[597px] max-w-[1260px]': mode !== 'addons' && !showOrderSummary,
          'min-h-[597px] max-w-[862px]': mode === 'addons' && !showOrderSummary,
          'min-h-[720px] max-w-[1260px]': mode === 'addons' && showOrderSummary,
          'max-w-[1440px]': mode === 'upgrade' && showOrderSummary,
        })}
      >
        {!paywallState.fetching && actionDiscounts ? (
          <>
            <PaywallRibbon discount={actionDiscounts.discount} />
            <PaywallXmasTree />
          </>
        ) : null}
        <PaywallCloseButton onClick={onClosePaywall} />
        <div
          data-cy-mode={mode}
          className={classNames('paywall__wrapper', {
            'max-w-[1260px] w-full': !showOrderSummary && mode !== 'addons',
            'max-w-[1092px] w-full': showOrderSummary,
            '!w-[862px]': mode === 'addons',
            'rounded-xl px-10 pt-8 pb-10': !showOrderSummary,
            'rounded-l-xl py-11': showOrderSummary,
            'px-6': showOrderSummary && mode !== 'addons',
            'flex flex-col px-10': mode === 'addons',
            'bg-white': mode !== 'addons' && !actionDiscounts,
            'bg-gray-100': mode === 'addons',
            'paywall_action_gradient': actionDiscounts,
            'paywall__wrapper_gradient': !actionDiscounts && mode !== 'addons' && mode !== 'upgrade' && !isAppsumo,
          })}
        >
          <PaywallHeader />
          {mode !== 'addons' ? (
            <>
              <PaywallPlans />
              <PaywallFooter />
            </>
          ) : (
            <>
              <PaywallAddons isOpen={true} />
              <div className="mt-3 flex grow items-end">
                <div
                  className={classNames(
                    'text-cyan-900 text-[13px] font-bold',
                    'inline-flex gap-2 px-1 py-2 ml-[3px]',
                    'cursor-pointer'
                  )}
                  onClick={onClosePaywall.bind(null, 'skip')}
                >
                  <div>{t('paywall.skip_for_now')}</div>
                  <Icon icon={IconsMap.ARROW_FORWARD} className="w-5" />
                </div>
              </div>
            </>
          )}
        </div>
        {showOrderSummary ? <PaywallOrderSummary /> : null}
        {paywallState.fetching || paywallState.fetching === null ? (
          <div className="absolute left-0 top-0 w-full h-full bg-white rounded-xl flex items-center justify-center z-10">
            <Spinner width={42} height={42} />
          </div>
        ) : null}
      </div>
      {paywallState.confirmError ? <PaywallErrorDialog /> : null}
    </PaywallContext.Provider>
  );
}

export default Paywall;
